import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSocialAccounts, getSocialPosts, type SocialPost } from "@/services/socialFeedService";
import {
  getMySocialLikes,
  getSocialLikeCounts,
  likeSocialPost,
  unlikeSocialPost,
} from "@/services/socialLikeService";
import { SocialFeed } from "@/components/social/SocialFeed";
import { SocialFeedSkeleton } from "@/components/social/SocialFeedSkeleton";
import Seo from "@/components/seo/Seo";
import AuthModal from "@/components/auth/AuthModal";
import { useAuth } from "@/context/AuthContext";
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

/**
 * Puts creators the viewer likes first, keeping a steady sprinkle of
 * creators they have never liked so the feed stays fresh.
 */
function personalise(posts: SocialPost[], likedIds: Set<string>): SocialPost[] {
  if (likedIds.size === 0) return posts;

  const score = new Map<string, number>();
  for (const post of posts) {
    if (likedIds.has(post.id)) {
      score.set(post.account_id, (score.get(post.account_id) ?? 0) + 1);
    }
  }

  const groups = new Map<string, SocialPost[]>();
  for (const post of posts) {
    const list = groups.get(post.account_id) ?? [];
    list.push(post);
    groups.set(post.account_id, list);
  }
  // Already seen posts go to the back of their creator's queue.
  for (const list of groups.values()) {
    list.sort((a, b) => Number(likedIds.has(a.id)) - Number(likedIds.has(b.id)));
  }

  const familiar = [...groups.keys()]
    .filter((id) => (score.get(id) ?? 0) > 0)
    .sort((a, b) => (score.get(b) ?? 0) - (score.get(a) ?? 0));
  const fresh = [...groups.keys()].filter((id) => (score.get(id) ?? 0) === 0);

  const out: SocialPost[] = [];
  let f = 0;
  let n = 0;
  while (out.length < posts.length) {
    const useFresh = fresh.length > 0 && out.length % 4 === 3;
    const pool = useFresh ? fresh : familiar;
    const other = useFresh ? familiar : fresh;
    let taken = false;
    for (let i = 0; i < pool.length + other.length; i += 1) {
      const source = i < pool.length ? pool : other;
      const cursor = i < pool.length ? (useFresh ? n : f) + i : i - pool.length;
      const key = source[cursor % Math.max(1, source.length)];
      const list = key ? groups.get(key) : undefined;
      const next = list?.shift();
      if (next) {
        out.push(next);
        if (useFresh) n += 1;
        else f += 1;
        taken = true;
        break;
      }
    }
    if (!taken) break;
  }
  return out;
}

const Social = () => {
  const t = useT();
  const { user, isAuthenticated } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [localLikes, setLocalLikes] = useState<Record<string, boolean>>({});

  const { data: accounts } = useQuery({
    queryKey: ["social-accounts"],
    queryFn: () => getSocialAccounts(),
  });

  const { data: posts, isLoading: loadingPosts } = useQuery({
    queryKey: ["social-posts", null],
    queryFn: () => getSocialPosts(),
  });

  const { data: myLikes } = useQuery({
    queryKey: ["social-my-likes", user?.id],
    queryFn: () => getMySocialLikes(user!.id),
    enabled: Boolean(user?.id),
  });

  const { data: counts } = useQuery({
    queryKey: ["social-like-counts", posts?.length ?? 0],
    queryFn: () => getSocialLikeCounts((posts ?? []).map((p) => p.id)),
    enabled: Boolean(posts && posts.length > 0),
  });

  const likedIds = useMemo(() => {
    const set = new Set(myLikes ?? []);
    for (const [id, on] of Object.entries(localLikes)) {
      if (on) set.add(id);
      else set.delete(id);
    }
    return set;
  }, [myLikes, localLikes]);

  const [likeDelta, setLikeDelta] = useState<Record<string, number>>({});
  const likeCounts = useMemo(() => {
    const map = new Map(counts ?? []);
    for (const [id, delta] of Object.entries(likeDelta)) {
      map.set(id, Math.max(0, (map.get(id) ?? 0) + delta));
    }
    return map;
  }, [counts, likeDelta]);

  // Order is decided once per visit so the feed does not jump while watching.
  const ordered = useMemo(
    () => personalise(posts ?? [], new Set(myLikes ?? [])),
    [posts, myLikes],
  );

  const toggleLike = async (postId: string) => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    const nowLiked = !likedIds.has(postId);
    setLocalLikes((prev) => ({ ...prev, [postId]: nowLiked }));
    setLikeDelta((prev) => ({ ...prev, [postId]: (prev[postId] ?? 0) + (nowLiked ? 1 : -1) }));
    try {
      if (nowLiked) await likeSocialPost(postId, user.id);
      else await unlikeSocialPost(postId, user.id);
    } catch {
      setLocalLikes((prev) => ({ ...prev, [postId]: !nowLiked }));
      setLikeDelta((prev) => ({ ...prev, [postId]: (prev[postId] ?? 0) + (nowLiked ? -1 : 1) }));
    }
  };

  return (
    <>
      <Seo
        title={t("auto.social-byteline-f2fc")}
        description={t("auto.creators-we-follow-on-tiktok-and-instagr-9fb9")}
        path="/social"
      />
      <div className="w-full bg-black">
        {loadingPosts || posts === undefined ? (
          <SocialFeedSkeleton />
        ) : ordered.length > 0 ? (
          <SocialFeed
            posts={ordered}
            accounts={accounts ?? []}
            likedIds={likedIds}
            likeCounts={likeCounts}
            onToggleLike={toggleLike}
          />
        ) : (
          <div className="flex h-[calc(100dvh-8rem)] flex-col items-center justify-center px-8 text-center">
            <h1 className="text-2xl font-semibold text-white"><T k="auto.nothing-here-yet-e892" /></h1>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-white/70">
              <T k="auto.posts-from-the-creators-we-follow-will-a-c4e8" />
            </p>
          </div>
        )}
      </div>
      {!isAuthenticated ? <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} /> : null}
    </>
  );
};

export default Social;
